import { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import StatsGrid from '@/components/StatsGrid';
import StatCard from '@/components/StatCard';
import api from '@/lib/api';
import { toast } from 'sonner';
import { FileText, Newspaper, Calendar, Briefcase, LogOut, Menu, X } from 'lucide-react';


const Dashboard = () => {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    submissions: 0,
    news: 0,
    events: 0,
    cases: 0
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const [submissionsRes, newsRes, eventsRes, casesRes] = await Promise.all([
        api.get(`/form-submissions`),
        api.get(`/news`),
        api.get(`/events`),
        api.get(`/cases/stats/summary`).catch(() => ({ data: { total: 0 } })),
      ]);

      setStats({
        submissions: submissionsRes.data.length,
        news: newsRes.data.length,
        events: eventsRes.data.length,
        cases: casesRes.data.total || 0
      });
    } catch (error) {
      console.error('Failed to fetch stats', error);
    }
  }, []);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    } else if (user) {
      if (!['admin', 'senior_partner', 'associate'].includes(user.role)) {
        toast.error('Access denied. Staff privileges required.');
        navigate('/');
        return;
      }
      fetchStats();
    }
  }, [user, loading, navigate, fetchStats]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="font-sans text-lg text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary">
      {/* Header */}
      <header className="bg-primary text-primary-foreground shadow-md">
        <div className="px-6 md:px-12 py-4 flex justify-between items-center">
          <h1 className="font-serif text-2xl font-bold" data-testid="dashboard-title">Forty Chambers Admin</h1>
          <div className="flex items-center space-x-4">
            <span className="hidden md:block font-sans text-sm">Welcome, {user.full_name}</span>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="hover:bg-accent/10"
              data-testid="logout-button"
              aria-label="Log out"
            >
              <LogOut size={20} />
            </Button>
            <button
              className="md:hidden focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle navigation menu"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`${mobileMenuOpen ? 'block' : 'hidden'} md:block w-64 bg-primary text-primary-foreground min-h-screen p-6`}>
          <nav className="space-y-2">
            <Link
              to="/dashboard"
              className="block px-4 py-3 hover:bg-accent/10 font-sans text-sm font-medium tracking-wide transition-colors"
              data-testid="nav-dashboard"
            >
              Dashboard
            </Link>
            <Link
              to="/cases"
              className="block px-4 py-3 hover:bg-accent/10 font-sans text-sm font-medium tracking-wide transition-colors"
              data-testid="nav-cases"
            >
              Case Management
            </Link>
            <Link
              to="/clients"
              className="block px-4 py-3 hover:bg-accent/10 font-sans text-sm font-medium tracking-wide transition-colors"
              data-testid="nav-clients"
            >
              Clients
            </Link>
            <Link
              to="/people"
              className="block px-4 py-3 hover:bg-accent/10 font-sans text-sm font-medium tracking-wide transition-colors"
              data-testid="nav-people"
            >
              People
            </Link>
            <Link
              to="/organizations"
              className="block px-4 py-3 hover:bg-accent/10 font-sans text-sm font-medium tracking-wide transition-colors"
              data-testid="nav-organizations"
            >
              Organizations
            </Link>
            <Link
              to="/invoices"
              className="block px-4 py-3 hover:bg-accent/10 font-sans text-sm font-medium tracking-wide transition-colors"
              data-testid="nav-invoices"
            >
              Invoices
            </Link>
            <Link
              to="/appointments"
              className="block px-4 py-3 hover:bg-accent/10 font-sans text-sm font-medium tracking-wide transition-colors"
              data-testid="nav-appointments"
            >
              Appointments
            </Link>
            <Link
              to="/submissions"
              className="block px-4 py-3 hover:bg-accent/10 font-sans text-sm font-medium tracking-wide transition-colors"
              data-testid="nav-submissions"
            >
              Form Submissions
            </Link>
            <Link
              to="/content"
              className="block px-4 py-3 hover:bg-accent/10 font-sans text-sm font-medium tracking-wide transition-colors"
              data-testid="nav-content"
            >
              Content Management
            </Link>
            <Link
              to="/campaigns"
              className="block px-4 py-3 hover:bg-accent/10 font-sans text-sm font-medium tracking-wide transition-colors"
              data-testid="nav-campaigns"
            >
              Campaigns
            </Link>
            <Link
              to="/form-builder"
              className="block px-4 py-3 hover:bg-accent/10 font-sans text-sm font-medium tracking-wide transition-colors"
              data-testid="nav-form-builder"
            >
              Form Builder
            </Link>
            {user?.role === 'admin' && (
              <Link
                to="/workflows"
                className="block px-4 py-3 hover:bg-accent/10 font-sans text-sm font-medium tracking-wide transition-colors"
                data-testid="nav-workflows"
              >
                Workflows
              </Link>
            )}
            {user?.role === 'admin' && (
              <Link
                to="/users"
                className="block px-4 py-3 hover:bg-accent/10 font-sans text-sm font-medium tracking-wide transition-colors"
                data-testid="nav-users"
              >
                User Management
              </Link>
            )}
            {user?.role === 'admin' && (
              <Link
                to="/audit-logs"
                className="block px-4 py-3 hover:bg-accent/10 font-sans text-sm font-medium tracking-wide transition-colors"
                data-testid="nav-audit-logs"
              >
                Audit Log
              </Link>
            )}
            <Link
              to="/"
              className="block px-4 py-3 hover:bg-accent/10 font-sans text-sm font-medium tracking-wide transition-colors"
              data-testid="nav-public-site"
            >
              Public Site
            </Link>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 md:p-12">
          <h2 className="font-serif text-4xl font-bold text-primary mb-8" data-testid="welcome-heading">
            Welcome, {user.full_name}
          </h2>

          {/* Stats Grid */}
          <StatsGrid cols={4} className="mb-12">
            <StatCard icon={Briefcase} value={stats.cases} label="Active Cases" testId="stat-card-cases" />
            <StatCard icon={FileText} value={stats.submissions} label="Form Submissions" testId="stat-card-submissions" />
            <StatCard icon={Newspaper} value={stats.news} label="News Articles" testId="stat-card-news" />
            <StatCard icon={Calendar} value={stats.events} label="Upcoming Events" testId="stat-card-events" />
          </StatsGrid>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="font-serif text-2xl font-bold text-primary">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Button asChild className="w-full bg-accent text-accent-foreground hover:bg-accent/90 py-6" data-testid="quick-action-cases">
                  <Link to="/cases">Manage Cases</Link>
                </Button>
                <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-6" data-testid="quick-action-submissions">
                  <Link to="/submissions">View Submissions</Link>
                </Button>
                <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-6" data-testid="quick-action-content">
                  <Link to="/content">Manage Content</Link>
                </Button>
                <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-6" data-testid="quick-action-campaigns">
                  <Link to="/campaigns">Email Campaigns</Link>
                </Button>
                <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-6" data-testid="quick-action-forms">
                  <Link to="/form-builder">Form Builder</Link>
                </Button>
                <Button asChild className="w-full bg-secondary text-primary hover:bg-secondary/90 py-6 border border-border" data-testid="quick-action-public-site">
                  <Link to="/">View Public Site</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;